// Waitlists and releases (accounts wave 6): a game can be listed as coming soon with an expected date; players join
// its waitlist in Threedom, and the creator releases it from the dashboard, which notifies everyone waiting (and
// everyone who hearted or followed the game). Times from accounts are unix seconds.

export interface ReleaseInfo {
  gameId: string;
  status: "released" | "coming_soon";
  /** expected release, unix seconds */
  releaseAt: number | null;
  /** unix seconds; null = never released through the waitlist */
  releasedAt: number | null;
  /** people on the waitlist */
  waiting: number;
}

export interface Released {
  gameId: string;
  status: "released";
  releasedAt: number | null;
  notified: number;
  alreadyReleased: boolean;
}

const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

/** accounts' `GET|PATCH /v1/creators/me/games/:id/waitlist|release`, read defensively. */
export function parseRelease(raw: unknown, gameId: string): ReleaseInfo {
  const r = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  return {
    gameId: typeof r.gameId === "string" ? r.gameId : gameId,
    status: r.status === "coming_soon" ? "coming_soon" : "released",
    releaseAt: num(r.releaseAt),
    releasedAt: num(r.releasedAt),
    waiting: Math.max(0, Math.floor(num(r.waiting) ?? 0)),
  };
}

export function parseReleased(raw: unknown, gameId: string): Released {
  const r = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  return {
    gameId: typeof r.gameId === "string" ? r.gameId : gameId,
    status: "released",
    releasedAt: num(r.releasedAt),
    notified: Math.max(0, Math.floor(num(r.notified) ?? 0)),
    alreadyReleased: r.alreadyReleased === true,
  };
}

/** A `datetime-local` value (the viewer's wall clock) → unix seconds, or null. */
export function localToUnix(value: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return null;
  const t = new Date(value).getTime(); // without a zone, the string is local time
  return Number.isFinite(t) ? Math.floor(t / 1000) : null;
}

/** unix seconds → a `datetime-local` value in the viewer's zone. */
export function unixToLocal(unix: number): string {
  const d = new Date(unix * 1000);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** What the creator can do now. Coming soon is possible only for a game that was never released. */
export function releaseActions(r: ReleaseInfo): { canRelease: boolean; canEditDate: boolean; canMarkComingSoon: boolean } {
  const soon = r.status === "coming_soon";
  return { canRelease: soon, canEditDate: soon, canMarkComingSoon: !soon && r.releasedAt === null };
}

/** "Release now" confirm text: who gets told. */
export function releaseConfirmText(title: string, waiting: number): string {
  const who =
    waiting === 0
      ? "Nobody is on the waitlist yet, but players who hearted or followed it are told."
      : `${waiting} ${waiting === 1 ? "person is" : "people are"} on the waitlist; they, and players who hearted or followed it, are told it's out.`;
  return `Release ${title} now? It becomes playable for everyone. ${who} This can't be undone.`;
}

/** Plain words for accounts' release errors. */
export function releaseError(code: string): string | null {
  const m: Record<string, string> = {
    already_released: "This game is already out, so it can't go back to coming soon.",
    not_found: "Accounts doesn't know this game, or it's hidden.",
    forbidden: "Only the game's creator can change its release.",
    not_creator: "Only the game's creator can change its release.",
    bad_request: "Accounts didn't accept that date. Pick a time in the future.",
  };
  return m[code] ?? null;
}
